import { useMemo, useState } from 'react'

import { CostCenterByLegalEntitiesDocument } from 'generated/resources/graphql'
import { debounce } from 'lodash'

import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'

import { useCostCenterAssignmentsContext } from 'modules/cost_centers/components/CostCenterAssigments/CostCenterAssignmentsContext'

export interface CostCenterOption {
  id: string
  name: string
}

interface Props {
  legalEntityIds: string[]
  excludeCostCenterIds: string[]
  employeeIds?: string[]
}

/**
 * Build a display name for a structured cost center from its dimensions.
 * Joins all non-null dimension names (family, function, role, workplace) with ' - '.
 * Falls back to the CC name if no dimensions are present.
 */
const getStructuredCostCenterName = (node: {
  code?: string | null
  name: string
  dimensions?: Array<{
    jobCatalogFamilyName?: string | null
    jobCatalogFunctionName?: string | null
    jobCatalogRoleName?: string | null
    workplaceName?: string | null
  }> | null
}): string => {
  const parts: string[] = []

  if (node.dimensions && node.dimensions.length > 0) {
    for (const dimension of node.dimensions) {
      if (dimension.jobCatalogFamilyName) parts.push(dimension.jobCatalogFamilyName)
      if (dimension.jobCatalogFunctionName) parts.push(dimension.jobCatalogFunctionName)
      if (dimension.jobCatalogRoleName) parts.push(dimension.jobCatalogRoleName)
      if (dimension.workplaceName) parts.push(dimension.workplaceName)
    }
  }

  const dimensionName = parts.length > 0 ? parts.join(' - ') : node.name
  return node.code ? `${node.code} - ${dimensionName}` : dimensionName
}

export const useCostCenterByLegalEntities = ({
  excludeCostCenterIds,
  legalEntityIds,
  employeeIds,
}: Props) => {
  const [search, setSearch] = useState('')
  const debouncedOnSearch = useMemo(
    () =>
      debounce((val: string) => {
        setSearch(val)
      }, 200),
    []
  )

  const clearSearch = useMemo(
    () => () => {
      setSearch('')
    },
    []
  )

  const { hasNewStructureEnabled } = useCostCenterAssignmentsContext()

  const { nodes, controls, loading } = usePaginatedQuery(CostCenterByLegalEntitiesDocument, {
    variables: {
      legalEntityIds,
      search,
      employeeIds: hasNewStructureEnabled ? employeeIds : undefined, // Only filter by employee in legacy mode
      sourceType: hasNewStructureEnabled ? 'structure_based' : 'legacy',
    },
    extractConnection: (data) => data.costCenters.costCentersConnection,
  })

  const costCenterOptions: CostCenterOption[] = nodes.map(
    (node): CostCenterOption => ({
      id: `${node.id}`,
      name: `${node.code ? `${node.code} - ${node.name}` : node.name}`,
    })
  )

  // Legacy mode: filter by excludeCostCenterIds
  const filteredCostCenterOptions = costCenterOptions.filter(
    (op) => !excludeCostCenterIds.includes(String(op.id))
  )

  // Structured mode: all structured cost centers with flattened dimension names
  const structuredCostCenterOptions: CostCenterOption[] = nodes.map(
    (node): CostCenterOption => ({
      id: `${node.id}`,
      name: getStructuredCostCenterName(node),
    })
  )

  return {
    controls,
    rawCostCenterOptions: costCenterOptions,
    debouncedOnSearch,
    clearSearch,
    filteredCostCenterOptions,
    structuredCostCenterOptions,
    hasNewStructureEnabled,
    loading,
  }
}
